import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import VendorCard from './VendorCard';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeaturedCarousel = ({ 
  title, 
  subtitle, 
  vendors = [], 
  onMessage, 
  onCheckAvailability, 
  onToggleFavorite,
  viewAllLink = '/category-browse'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const itemsPerView = {
    mobile: 1,
    tablet: 2,
    desktop: 3,
    large: 4
  };

  const getVisibleItems = () => {
    if (typeof window === 'undefined') return itemsPerView.desktop;
    
    const width = window.innerWidth;
    if (width >= 1280) return itemsPerView.large;
    if (width >= 1024) return itemsPerView.desktop;
    if (width >= 640) return itemsPerView.tablet;
    return itemsPerView.mobile;
  };

  const [visibleItems, setVisibleItems] = useState(getVisibleItems());

  React.useEffect(() => {
    const handleResize = () => {
      setVisibleItems(getVisibleItems());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, vendors.length - visibleItems);
  const canScrollLeft = currentIndex > 0;
  const canScrollRight = currentIndex < maxIndex;

  // Touch event handlers for smooth vertical scrolling
  const handleTouchStart = (e) => {
    setIsDragging(true);
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    // Allow horizontal scrolling within carousel
    const touch = e.touches[0];
    const carousel = scrollContainerRef.current;
    if (carousel) {
      const rect = carousel.getBoundingClientRect();
      const isWithinCarousel = touch.clientX >= rect.left && touch.clientX <= rect.right;
      
      if (isWithinCarousel) {
        // Prevent vertical scrolling when touching carousel
        e.preventDefault();
      }
    }
  };

  const scrollLeft = () => {
    if (canScrollLeft) {
      setCurrentIndex(Math.max(0, currentIndex - 1));
    }
  };

  const scrollRight = () => {
    if (canScrollRight) {
      setCurrentIndex(Math.min(maxIndex, currentIndex + 1));
    }
  };

  const scrollToIndex = (index) => {
    setCurrentIndex(Math.max(0, Math.min(maxIndex, index)));
  };

  if (!vendors || vendors.length === 0) {
    return null;
  }

  return (
    <div className="bg-white py-12 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary">
              {title}
            </h2>
            {subtitle && (
              <p className="text-text-secondary font-caption">
                {subtitle}
              </p>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {/* Navigation Buttons */}
            <div className="hidden sm:flex items-center space-x-2">
              <button
                onClick={scrollLeft}
                disabled={!canScrollLeft}
                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  canScrollLeft
                    ? 'border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                    : 'border-border text-text-muted cursor-not-allowed'
                }`}
              >
                <Icon name="ChevronLeft" size={20} color="currentColor" />
              </button>
              <button
                onClick={scrollRight}
                disabled={!canScrollRight}
                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  canScrollRight
                    ? 'border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                    : 'border-border text-text-muted cursor-not-allowed'
                }`}
              >
                <Icon name="ChevronRight" size={20} color="currentColor" />
              </button>
            </div>

            {/* View All Link */}
            <Link to={viewAllLink}>
              <Button variant="outline" size="sm" iconName="ArrowRight" iconPosition="right" iconSize={16}>
                View All
              </Button>
            </Link>
          </div>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          <div 
            ref={scrollContainerRef}
            className="overflow-hidden"
            style={{ 
              touchAction: 'pan-x',
              WebkitOverflowScrolling: 'touch'
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
          >
            <div 
              className="flex transition-transform duration-300 ease-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / visibleItems)}%)`,
                width: `${(vendors.length / visibleItems) * 100}%`
              }}
            >
              {vendors.map((vendor, index) => (
                <div
                  key={vendor.id}
                  className="flex-shrink-0 px-3"
                  style={{ width: `${100 / vendors.length}%` }}
                >
                  <VendorCard
                    vendor={vendor}
                    onMessage={onMessage}
                    onCheckAvailability={onCheckAvailability}
                    onToggleFavorite={onToggleFavorite}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="sm:hidden flex items-center justify-center space-x-4 mt-6">
            <button
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                canScrollLeft
                  ? 'border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                  : 'border-border text-text-muted cursor-not-allowed'
              }`}
            >
              <Icon name="ChevronLeft" size={24} color="currentColor" />
            </button>
            
            {/* Dots Indicator */}
            <div className="flex items-center space-x-2">
              {[...Array(maxIndex + 1)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    index === currentIndex ? 'bg-primary w-6' : 'bg-border'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={scrollRight}
              disabled={!canScrollRight}
              className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                canScrollRight
                  ? 'border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                  : 'border-border text-text-muted cursor-not-allowed'
              }`}
            >
              <Icon name="ChevronRight" size={24} color="currentColor" />
            </button>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="hidden sm:flex items-center justify-center space-x-2 mt-8">
          {[...Array(maxIndex + 1)].map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`h-1 rounded-full transition-all duration-200 ${
                index === currentIndex ? 'bg-primary w-8' : 'bg-border w-2'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedCarousel;